import { Box } from '@chakra-ui/react';
import { Table } from '@tanstack/react-table';
import React from 'react';
import { AiFillCaretDown, AiFillCaretUp, AiOutlineLine } from 'react-icons/ai';

import { FooterDataProps } from '../../../../interfaces';
import { useTabsViewsContext } from '../../../TabsViews';
import { CellFooter } from '../Footer/styles';

const DEFAULT_HEADER_STYLE = {
  flex: 1,
};

interface SingleFooterProps {
  footer: FooterDataProps[];
  table: Table<any>;
  footerBackgroundColor?: string;
  footerBorderColor?: string;
  footerTextColor?: string;
  footerTextWeight?: string | number;
  footerFontSize?: string;
  footerIconType?: 'positive' | 'negative' | 'equal';
  footerIconColor?: string;
  footerIconSize?: number;
  restColumns: Record<string, any>;
}

const SingleFooter: React.FC<SingleFooterProps> = ({
  footer,
  table,
  footerBackgroundColor,
  footerBorderColor,
  footerTextColor,
  footerTextWeight,
  footerFontSize,
  footerIconType,
  footerIconColor,
  footerIconSize,
  restColumns,
}) => {
  const { hasTabs, activeTabs } = useTabsViewsContext();

  // Get the last header group to access column information for alignment
  const headerGroups = table.getHeaderGroups();
  const lastHeaderGroup = headerGroups[headerGroups.length - 1];

  // Create a map of column headers for quick lookup
  const columnMap = new Map();
  lastHeaderGroup.headers.forEach((header) => {
    const columnId = header.id;
    const originalId = (header as any).originalId;
    columnMap.set(columnId, header);
    if (originalId && originalId !== columnId) {
      columnMap.set(originalId, header);
    }
  });

  return (
    <div className="innerFooter">
      {footer.map((footerItem, index) => {
        const {
          hasArrow,
          header,
          justifyContent,
          minWidth,
          key,
          width,
          arrowDirection,
          arrowColor,
          arrowSize,
        } = footerItem;

        // Find the corresponding column header to get layout properties
        const matchingHeader = columnMap.get(key);
        const columnId = matchingHeader?.id || key;
        const originalId = matchingHeader ? (matchingHeader as any).originalId : null;

        // Get column configuration from restColumns for proper alignment
        const columnConfig = restColumns[columnId] || restColumns[originalId] || restColumns[key];
        const { flex = 1 } = columnConfig ?? DEFAULT_HEADER_STYLE;

        // Calculate flex size considering tabs
        const flexSize =
          index === 0 && hasTabs
            ? 1 +
              Array.from(activeTabs?.values())?.filter(
                (item) => item?.selected && item?.selected?.length > 0,
              )?.length
            : flex;

        // Render footer content
        const footerComponent = typeof header === 'function' && matchingHeader
          ? header(matchingHeader)
          : header;

        return (
          <CellFooter
            key={key || `footer-${index}`}
            width={width}
            minWidth={minWidth || 0}
            justifyContent={justifyContent}
            footerBackgroundColor={footerBackgroundColor || 'transparent'}
            footerBorderColor={footerBorderColor || 'transparent'}
            footerTextColor={footerTextColor || '#7d7d7d'}
            footerTextWeight={footerTextWeight || '#7d7d7d'}
            footerFontSize={footerFontSize || '10px'}
            style={{ flex: flexSize }}
          >
            {hasArrow &&
              (arrowDirection ? (
                <Box
                  display="flex"
                  justifyContent={justifyContent ?? 'center'}
                  width="100%"
                >
                  <span>
                    {arrowDirection === 'positive' && (
                      <AiFillCaretUp
                        color={arrowColor ?? '#00b400'}
                        size={arrowSize ?? 18}
                      />
                    )}
                    {arrowDirection === 'negative' && (
                      <AiFillCaretDown
                        color={arrowColor ?? '#FF2E2E'}
                        size={arrowSize ?? 18}
                      />
                    )}
                    {arrowDirection === 'equal' && (
                      <AiOutlineLine
                        color={arrowColor ?? '#FFEF00'}
                        size={arrowSize ?? 18}
                      />
                    )}
                  </span>
                  <span>{header}</span>
                </Box>
              ) : (
                <Box
                  display="flex"
                  justifyContent={justifyContent ?? 'center'}
                  width="100%"
                >
                  <span>
                    {footerIconType === 'positive' && (
                      <AiFillCaretUp
                        color={footerIconColor}
                        size={footerIconSize}
                      />
                    )}
                    {footerIconType === 'negative' && (
                      <AiFillCaretDown
                        color={footerIconColor}
                        size={footerIconSize}
                      />
                    )}
                    {footerIconType === 'equal' && (
                      <AiOutlineLine
                        color={footerIconColor}
                        size={footerIconSize}
                      />
                    )}
                  </span>
                  <span>{header}</span>
                </Box>
              ))}
            {!hasArrow && footerComponent}
          </CellFooter>
        );
      })}
    </div>
  );
};

export default SingleFooter;
